import React, { useEffect, useState, useCallback } from 'react';
import { Star, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { listCurations, upsertCuration, deleteCuration } from '../lib/curatedBooksApi';

// Shop Curation: lets any signed-in reader add a short description + a
// 0-5 star rating for a book (identified by book_cache.external_id, the
// same shared book identity already used by AddBookModal.js / user
// curations -- see migrations/40_curated_books.sql for why this isn't
// tied to the per-user public.books table). Reads are public; writes
// require auth and are always scoped to the current session's user_id,
// enforced both client-side (curatedBooksApi.js) and by RLS.
//
// `bookId` here must be the book_cache.external_id (Google Books volume
// id), not a public.books row id.
const BookCuration = ({ bookId }) => {
  const { user } = useAuth();
  const [curations, setCurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState('');
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    try {
      const data = await listCurations(bookId);
      setCurations(data);
      const mine = user && data.find(c => c.user_id === user.id);
      if (mine) {
        setDescription(mine.description || '');
        setRating(mine.rating || 0);
      }
    } catch (e) {
      console.error('Failed to load curations', e);
    } finally {
      setLoading(false);
    }
  }, [bookId, user]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await upsertCuration({ book_id: bookId, description, rating });
      await load();
    } catch (e) {
      setError(e.message || 'Could not save your curation.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCuration(id);
      await load();
    } catch (e) {
      setError(e.message || 'Could not remove that curation.');
    }
  };

  if (!bookId) return null;

  return (
    <div style={sectionStyle}>
      <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#666' }}>Shop Curation</h4>

      {user && (
        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={starRowStyle}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                onClick={() => setRating(n)}
                style={starButtonStyle}
                aria-label={`Rate ${n} star${n === 1 ? '' : 's'}`}
              >
                <Star size={18} fill={n <= rating ? '#ffc107' : 'none'} color="#ffc107" />
              </button>
            ))}
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a short description or note about this book..."
            style={textareaStyle}
            maxLength={500}
          />
          {error && <p style={errorStyle}>{error}</p>}
          <button type="submit" disabled={submitting} style={submitButtonStyle}>
            {submitting ? 'Saving...' : 'Save Curation'}
          </button>
        </form>
      )}

      {loading ? (
        <p style={metaTextStyle}>Loading curations...</p>
      ) : curations.length === 0 ? (
        <p style={metaTextStyle}>No curations yet. Be the first to add one!</p>
      ) : (
        <ul style={listStyle}>
          {curations.map((c) => (
            <li key={c.id} style={itemStyle}>
              <div style={itemHeaderStyle}>
                <div style={starRowStyle}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      size={13}
                      fill={c.rating && n <= c.rating ? '#ffc107' : 'none'}
                      color="#ffc107"
                    />
                  ))}
                </div>
                {user && c.user_id === user.id && (
                  <button
                    onClick={() => handleDelete(c.id)}
                    style={deleteButtonStyle}
                    aria-label="Delete your curation"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              {c.description && <p style={descriptionStyle}>{c.description}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const sectionStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  backgroundColor: 'var(--bg-app)',
  padding: '12px',
  borderRadius: '10px',
  border: '1px solid var(--border)',
};

const starRowStyle = {
  display: 'flex',
  gap: '2px',
};

const starButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
};

const textareaStyle = {
  width: '100%',
  minHeight: '60px',
  borderRadius: '6px',
  border: '1px solid var(--border)',
  padding: '8px',
  fontSize: '0.85rem',
  fontFamily: 'inherit',
  backgroundColor: 'var(--bg-card)',
  color: 'var(--text-main)',
  resize: 'vertical',
};

const submitButtonStyle = {
  backgroundColor: 'var(--accent)',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '0.85rem',
  alignSelf: 'flex-start',
};

const errorStyle = {
  color: '#ff5252',
  fontSize: '0.75rem',
  margin: 0,
};

const listStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const itemStyle = {
  padding: '8px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-card)',
};

const itemHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const descriptionStyle = {
  margin: '6px 0 0 0',
  fontSize: '0.8rem',
  color: 'var(--text-soft)',
};

const deleteButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#ff5252',
  padding: '2px',
};

const metaTextStyle = {
  fontSize: '0.8rem',
  color: 'var(--text-soft)',
  fontStyle: 'italic',
};

export default BookCuration;
